import { useId, useRef } from 'react';
import {
  ComboBox as AriaComboBox,
  type ComboBoxProps as AriaComboBoxProps,
  ListBox,
  type ListBoxItemProps,
  type ValidationResult,
  Button,
  Group,
  Input,
  Label as AriaLabel,
} from 'react-aria-components';
import { Description, FieldError } from './field';
import { DropdownItem, DropdownSection, type DropdownSectionProps } from './list-box';
import { Popover } from './popover';
import { composeTailwindRenderProps } from './component-utils';

import { Label } from './field';
import { ScrollArea } from './scroll-area';
import { InputChevron } from './input-chevron';
import { TouchTarget } from './touch-target';
import { isDomain } from 'utils/domain';

type ComboBoxItem = {
  label: string;
  value: string;
};

export interface ComboBoxProps<T extends ComboBoxItem> extends Omit<AriaComboBoxProps<T>, 'children'> {
  label?: string;
  description?: string | null;
  errorMessage?: string | ((validation: ValidationResult) => string);
  children?: React.ReactNode | ((item: T) => React.ReactNode);
}

const isLab = isDomain('camperlab');
// border px-2 text-copy placeholder:text-sm placeholder:text-disabled lab:h-8 lg:text-sm lab:lg:h-6
export function ComboBox<T extends ComboBoxItem>({ label, description, errorMessage, children, items, ...props }: ComboBoxProps<T>) {
  const id = useId();
  const triggerRef = useRef<HTMLDivElement>(null);

  return (
    <AriaComboBox {...props} className={composeTailwindRenderProps(props.className, 'group relative flex flex-col gap-1')}>
      {isLab ?
        <Label>{label}</Label>
      : null}
      <Group
        ref={triggerRef}
        className="relative flex h-[52px] w-full gap-1 border invalid:border-error focus-within:border-dark invalid:focus-within:border-error focus-visible:ring-2 focus-visible:ring-dark focus-visible:ring-opacity-60 lab:h-8 lab:lg:h-6"
      >
        <Input
          placeholder={' '}
          className="peer h-full w-full pl-3 pt-1.5 text-sm outline-none placeholder:text-transparent lab:pl-1.5 lab:pt-0 lab:text-sm lab:lg:text-xs"
        />
        {!isLab ?
          <AriaLabel className="absolute bottom-[30px] left-3 text-xxs text-muted transition-all peer-placeholder-shown:bottom-[16px] peer-placeholder-shown:text-sm peer-focus:bottom-[30px] peer-focus:text-xxs">
            {label}
          </AriaLabel>
        : null}
        <Button className="rounded-xs relative flex items-center justify-center self-center pr-3 outline-offset-0 lab:pr-1.5">
          <TouchTarget className="size-5" />
          <InputChevron className="group-open:rotate-180" />
        </Button>
      </Group>
      {description && <Description>{description}</Description>}
      {errorMessage ?
        <FieldError className="absolute inset-x-0 top-full z-10 border border-t-0 border-error bg-clear px-2 py-1 text-center">{errorMessage}</FieldError>
      : <FieldError className="absolute inset-x-0 top-full z-10 border border-t-0 border-error bg-clear px-2 py-1 text-center">
          {({ validationErrors }) =>
            validationErrors.map((v, i) => (
              <span className="block" key={`${id}-${i}`}>
                {v}
              </span>
            ))
          }
        </FieldError>
      }
      <Popover triggerRef={triggerRef} className="min-w-[--trigger-width]" border={isLab}>
        <ScrollArea className="overflow-hidden">
          <ListBox items={items} className="max-h-52 min-h-0">
            {children ??
              ((item) => (
                <ComboBoxItem id={item.value} value={item}>
                  {item.label}
                </ComboBoxItem>
              ))}
          </ListBox>
        </ScrollArea>
      </Popover>
    </AriaComboBox>
  );
}

export function ComboBoxItem(props: ListBoxItemProps) {
  return <DropdownItem {...props} />;
}

export function ComboBoxSection<T extends object>(props: DropdownSectionProps<T>) {
  return <DropdownSection {...props} />;
}
